import { useContext, useEffect, useState, createContext } from 'react'
import { useSelector } from 'react-redux'

import { Box, makeStyles, Typography } from '@material-ui/core'
import { ExtensionOnly, PlaybookSearch, PlaybooksFooter } from 'UI/Components'

import {
    isAllSpacesSelected,
    PlaybookFilters,
    useFilterState,
} from './PlaybookFilters'
import { PaginatedPlaybooks } from './PaginatedPlaybooks'
import { mainSpaceOption } from './PlaybookFilters/SpaceFilter'

import { RootState } from 'ducks/rootReducer'

import { AppContext } from 'app/AppProvider'
import { useBoolean } from 'hooks'

import { getFromExtLocalStorage } from 'services/extServices'
import { SPACE_FILTER_STATE } from 'background/constants'

const useStyles = makeStyles(() => ({
    playbooksTabWrapper: {
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    playbooksTabBody: {
        overflow: 'auto',
        flexGrow: 1,
    },
}))

export const SearchByAppContext = createContext({
    setSearchResult: (result: boolean) => {},
    setSearchWithDefaultTagsResult: (result: boolean) => {},
})

export const isMainSpace = spaces => {
    if (spaces.includes('ALL')) {
        return false
    }

    return spaces.some(it => it === 'mainSpace')
}
export const generateSpaceIds = (spaces: string[]) => {
    if (spaces.includes('ALL')) {
        return []
    }

    return spaces.filter(it => it !== 'mainSpace')
}

export const PagePlaybooks = ({ predefinedSearch, defaultFilters }) => {
    const classes = useStyles()
    const resultIsEmpty = useBoolean()
    const resultWithDefaultTagsIsEmpty = useBoolean()

    const [searchText, setSearchText] = useState<null | string>(null)

    const { firstInstall } = useContext(AppContext)
    const shouldAddDemoTag =
        firstInstall.isTrue && window.location.host.includes('guidde.co')

    const appFilter = useFilterState<string>('')
    const tagsFilter = useFilterState<Array<string>>(
        shouldAddDemoTag ? ['getting-started'] : []
    )
    const spaceFilter = useFilterState<string[]>([])

    const apps = useSelector((state: RootState) => state.apps)
    const userSpaces = useSelector((state: RootState) => state.spaces)

    const selectedAppName = apps.find(
        app => app.id === appFilter.state || app.id === appFilter.defaultState
    )?.title

    const handleSpaceSelection = selectedSpaces => {
        if (isAllSpacesSelected(selectedSpaces)) {
            return
        }

        appFilter.resetState()
        tagsFilter.resetState()
    }

    useEffect(() => {
        const userSpacesIds = [
            'ALL',
            mainSpaceOption.value,
            ...Object.keys(userSpaces || {}),
        ]

        getFromExtLocalStorage([SPACE_FILTER_STATE], result => {
            if (!result[SPACE_FILTER_STATE]) return

            const filteredSpaces = result[SPACE_FILTER_STATE].filter(
                savedSpaceId => userSpacesIds.includes(savedSpaceId)
            )

            spaceFilter.updateState(filteredSpaces)
            handleSpaceSelection(filteredSpaces)
        })

        if (!chrome?.storage) {
            return
        }

        const listener = (storage, storageType) => {
            const selectedSpaces = storage[SPACE_FILTER_STATE]?.newValue

            if (storageType !== 'local' || !selectedSpaces) {
                return
            }

            spaceFilter.updateState(selectedSpaces)
            handleSpaceSelection(selectedSpaces)
        }

        chrome.storage.onChanged.addListener(listener)

        return () => chrome.storage.onChanged.removeListener(listener)

        // we set default value as last selected only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSpaces])

    useEffect(() => {
        appFilter.initDefaultState(defaultFilters.app, '')
        tagsFilter.initDefaultState(
            defaultFilters.tags,
            shouldAddDemoTag ? ['getting-started'] : []
        )
        handleSpaceSelection(spaceFilter.state)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFilters.app, defaultFilters.tags]) // Ignore effect on callback changes

    return (
        <Box className={classes.playbooksTabWrapper}>
            <Box p={3} className={classes.playbooksTabBody}>
                <PlaybookSearch
                    predefinedSearch={predefinedSearch}
                    appFilter={appFilter}
                    onSearchChange={setSearchText}
                    selectedAppName={selectedAppName}
                    spaceIds={spaceFilter.state}
                />

                <Box my={2}>
                    <PlaybookFilters
                        appFilter={appFilter}
                        tagsFilter={tagsFilter}
                        spaceFilter={spaceFilter}
                    />
                </Box>

                <Box>
                    <Typography>
                        <b>
                            {(selectedAppName && resultIsEmpty.isTrue) ||
                            (selectedAppName &&
                                resultWithDefaultTagsIsEmpty.isTrue)
                                ? `Top results for ${selectedAppName}`
                                : 'Recommended for you'}
                        </b>
                    </Typography>

                    <SearchByAppContext.Provider
                        value={{
                            setSearchResult: resultIsEmpty.set,
                            setSearchWithDefaultTagsResult:
                                resultWithDefaultTagsIsEmpty.set,
                        }}
                    >
                        <PaginatedPlaybooks
                            appFilter={appFilter}
                            tagsFilter={tagsFilter}
                            spaceFilter={spaceFilter}
                        />
                    </SearchByAppContext.Provider>
                </Box>
            </Box>
            <ExtensionOnly>
                <PlaybooksFooter
                    searchText={searchText}
                    appFilter={appFilter}
                    tagsFilter={tagsFilter}
                />
            </ExtensionOnly>
        </Box>
    )
}
